import { useState } from "react";

interface BookingFormProps {
  onBooked: () => void;
}

function BookingForm({ onBooked }: BookingFormProps) {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [selectedSlot, setSelectedSlot] = useState<string>("");

  const SLOTS = [
    "Hoy, lo antes posible (me llaman en &lt;2h hábiles)",
    "Mañana por la mañana (9:00 – 12:00)",
    "Mañana por la tarde (15:00 – 18:00)",
    "Esta semana, a convenir",
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedSlot) return;
    setSending(true);
    setError("");
    const form = e.currentTarget;
    const data = new URLSearchParams();
    data.append("nombre", (form.elements.namedItem("nombre") as HTMLInputElement).value);
    data.append("email", (form.elements.namedItem("email") as HTMLInputElement).value);
    data.append("telefono", (form.elements.namedItem("telefono") as HTMLInputElement).value);
    data.append("franja_horaria", selectedSlot.replace(/&lt;/g, "<"));
    data.append("notas", (form.elements.namedItem("notas") as HTMLTextAreaElement).value);
    try {
      const res = await fetch("https://readdy.ai/api/form/d6vbv2nb245v7h34olq0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setSent(true);
        onBooked();
      } else {
        setError("Hubo un problema al enviar. Inténtalo de nuevo.");
      }
    } catch {
      setError("Error de conexión. Inténtalo de nuevo.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="flex flex-col items-center justify-center py-8 px-4 text-center bg-amber-50 border border-amber-100 rounded-2xl">
        <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mb-3">
          <i className="ri-calendar-check-line text-amber-500 text-2xl" />
        </div>
        <p className="text-sm font-bold text-stone-800 mb-1">¡Reserva confirmada!</p>
        <p className="text-xs text-stone-500 leading-relaxed max-w-xs">
          Tu account manager te contactará en la franja seleccionada. Revisa tu email para la confirmación.
        </p>
      </div>
    );
  }

  const inputClass =
    "w-full px-3 py-2 text-sm bg-white border border-stone-200 rounded-xl text-stone-700 placeholder-stone-300 focus:outline-none focus:border-amber-300 focus:ring-1 focus:ring-amber-200 transition-all";
  const labelClass = "block text-xs font-bold text-stone-600 mb-1";

  return (
    <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>Nombre *</label>
          <input name="nombre" type="text" required placeholder="Tu nombre" className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Teléfono *</label>
          <input name="telefono" type="tel" required placeholder="+34 6XX XXX XXX" className={inputClass} />
        </div>
      </div>
      <div>
        <label className={labelClass}>Email *</label>
        <input name="email" type="email" required placeholder="tu@email.com" className={inputClass} />
      </div>
      <div>
        <label className={labelClass}>Franja horaria preferida *</label>
        <div className="grid sm:grid-cols-2 gap-2 mt-1">
          {SLOTS.map((slot) => (
            <button
              key={slot}
              type="button"
              onClick={() => setSelectedSlot(slot)}
              className={`text-left px-3 py-2.5 text-xs rounded-xl border transition-all cursor-pointer leading-snug ${
                selectedSlot === slot
                  ? "bg-amber-500 border-amber-500 text-white font-semibold"
                  : "bg-white border-stone-200 text-stone-600 hover:border-amber-300"
              }`}
              dangerouslySetInnerHTML={{ __html: slot }}
            />
          ))}
        </div>
      </div>
      <div>
        <label className={labelClass}>Notas adicionales (opcional)</label>
        <textarea
          name="notas"
          rows={2}
          maxLength={500}
          placeholder="¿Tienes algún CRM concreto o sistema específico que integrar? Cuéntanoslo."
          className={`${inputClass} resize-none`}
        />
      </div>
      {error && (
        <p className="text-xs text-red-500 flex items-center gap-1.5">
          <i className="ri-error-warning-line" />
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={sending || !selectedSlot}
        className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-amber-500 hover:bg-amber-400 disabled:bg-stone-200 disabled:text-stone-400 text-white text-sm font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
      >
        {sending ? (
          <>
            <i className="ri-loader-4-line animate-spin" />
            Enviando...
          </>
        ) : (
          <>
            <i className="ri-calendar-line" />
            Reservar llamada de onboarding
          </>
        )}
      </button>
    </form>
  );
}

export function PremiumOnboardingCallDetails({
  onBooked,
}: {
  onBooked: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="p-3 bg-amber-50 border border-amber-100 rounded-xl flex items-start gap-3">
        <i className="ri-vip-crown-line text-amber-500 text-base flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-700 leading-relaxed">
          <strong>Incluido en tu plan Premium.</strong> Un miembro del equipo Auria te llamará para configurar juntos Canvas Memory, Branded Call ID, desvíos e integración CRM. Tú no tienes que hacer nada técnico.
        </p>
      </div>
      <div>
        <p className="text-xs font-bold text-stone-500 uppercase tracking-widest mb-3">Qué cubre la llamada</p>
        <div className="grid sm:grid-cols-2 gap-2">
          {[
            { icon: "ri-file-text-line", text: "Canvas Memory completo (tu negocio, horarios, servicios)" },
            { icon: "ri-phone-line", text: "Activación del desvío de llamadas guiada en directo" },
            { icon: "ri-database-2-line", text: "Integración CRM con FastMCP (si aplica)" },
            { icon: "ri-fingerprint-line", text: "Configuración de Branded Call ID" },
            { icon: "ri-test-tube-line", text: "Llamada de prueba en directo al finalizar" },
            { icon: "ri-question-answer-line", text: "Resolución de todas tus dudas al momento" },
          ].map((item) => (
            <div key={item.text} className="flex items-start gap-2.5 p-2.5 bg-stone-50 rounded-xl border border-stone-100">
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                <i className={`${item.icon} text-amber-500 text-sm`} />
              </div>
              <span className="text-xs text-stone-600 leading-relaxed">{item.text}</span>
            </div>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs font-bold text-stone-500 uppercase tracking-widest mb-3">Reserva tu llamada</p>
        <BookingForm onBooked={onBooked} />
      </div>
    </div>
  );
}

export function PremiumCRMDetails() {
  return (
    <div className="space-y-4">
      <div className="p-3 bg-stone-50 border border-stone-100 rounded-xl flex items-start gap-3">
        <i className="ri-information-line text-amber-500 text-base flex-shrink-0 mt-0.5" />
        <p className="text-xs text-stone-600 leading-relaxed">
          <strong>Este paso lo configuramos juntos en la llamada de onboarding.</strong> Si quieres preparar la información de antemano, aquí tienes lo que necesitamos saber.
        </p>
      </div>
      <div>
        <p className="text-xs font-bold text-stone-500 uppercase tracking-widest mb-3">CRMs y sistemas compatibles</p>
        <div className="grid sm:grid-cols-3 gap-2">
          {[
            { name: "HubSpot", icon: "ri-hub-line" },
            { name: "Salesforce", icon: "ri-cloud-line" },
            { name: "Zoho CRM", icon: "ri-bar-chart-box-line" },
            { name: "Pipedrive", icon: "ri-git-branch-line" },
            { name: "Monday.com", icon: "ri-grid-line" },
            { name: "API propia", icon: "ri-code-s-slash-line" },
          ].map((crm) => (
            <div key={crm.name} className="flex items-center gap-2 p-3 bg-white border border-stone-100 rounded-xl">
              <div className="w-7 h-7 flex items-center justify-center">
                <i className={`${crm.icon} text-stone-400 text-base`} />
              </div>
              <span className="text-xs font-semibold text-stone-700">{crm.name}</span>
            </div>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">Qué hace la integración</p>
        <ul className="space-y-2">
          {[
            "Crea automáticamente un lead en tu CRM por cada llamada gestionada por Auria",
            "Sincroniza las citas agendadas como tareas o eventos en tu CRM",
            "Envía el resumen de la llamada y la transcripción al historial del contacto",
            "Actualiza el estado del lead si ya existe en tu base de datos",
          ].map((item) => (
            <li key={item} className="flex items-start gap-2.5 text-xs text-stone-600">
              <i className="ri-check-line text-emerald-500 text-sm flex-shrink-0 mt-0.5" />
              {item}
            </li>
          ))}
        </ul>
      </div>
      <div className="p-3 bg-amber-50 border border-amber-100 rounded-xl text-xs text-amber-700 leading-relaxed">
        <strong>¿Tienes un sistema diferente?</strong> No hay problema — FastMCP se conecta a cualquier API REST. Cuéntanos en la llamada de onboarding qué sistema usas y lo integramos.
      </div>
    </div>
  );
}
